// hooks/useMobileCarrierScanner.js
import React, { useState, useCallback, useMemo } from "react";
import { MASK_TYPE, Icons, IconButton, InputAdornment } from "@curves/ui-base";
import MobileCarrierBarCodeDialog from "@icoach/components/barCode/MobileCarrierBarCodeDialog";
import { isEqual } from "@util/utils";

/**
 * 📲 useTextInputCarrier - 手機載具條碼掃描 Hook
 *
 * 提供在特定 `maskType === MASK_TYPE.MB` 情況下的手機載具掃描功能。
 * 使用者可透過 input 欄位右側的圖示開啟條碼掃描器，並將結果帶入 input 欄位。
 *
 * 🧩 建議與 TextField 搭配使用，依 maskType 自動啟用掃描功能。
 *
 * @function useTextInputCarrier
 * @param {Function} setInputValue - 將掃描結果設定進外部欄位的函式
 * @param {Object}   [options= {}] - 掃描器選項
 * @param {boolean}  [options.readOnly=false] - 是否唯讀，唯讀時掃描器按鈕將隱藏
 * @param {boolean}  [options.disabled=false] - 是否禁用，禁用時掃描器按鈕將隱藏
 * @param {string}   [options.maskType] - 欄位輸入類型（必填，僅當為 `MASK_TYPE.MB` 時啟用掃描器）
 *
 * @returns {{
 *   renderEndAdornment: JSX.Element,
 *   renderDialog: JSX.Element
 * }} 返回包含 EndAdornment 掃描按鈕與掃描視窗 Dialog 的元件函式，用於組合在自訂 TextField 中
 *
 * @example
 * const { renderEndAdornment, renderDialog } = useTextInputCarrier(setValue, { maskType });
 *
 * return (
 *   <>
 *     <TextField
 *       label="手機載具"
 *       inputProps={{ maskType: MASK_TYPE.MB }}
 *       InputProps={{ endAdornment: renderEndAdornment }}
 *     />
 *     {renderDialog}
 *   </>
 * );
 */
const useTextInputCarrier = (setInputValue, options = {}) => {
	const { readOnly = false, maskType, disabled = false } = options;
	const [isOpen, setOpen] = useState(false);
	const isCarrierType = isEqual(maskType, MASK_TYPE.MB);

	/** 開啟掃描器 */
	const handleOpenScanner = useCallback(
		() => {
			setOpen(true);
		},
		// eslint-disable-next-line
		[]
	);

	/** 彈窗儲存送出反應 */
	const handleConfirmClick = useCallback(
		text => {
			setInputValue(text);
		},
		// eslint-disable-next-line
		[setInputValue]
	);

	/** 關閉掃描器 */
	const handleCloseScanner = useCallback(
		() => {
			setOpen(false);
		},
		// eslint-disable-next-line
		[]
	);

	/** 渲染 Input 的 EndAdornment */
	const renderEndAdornment = useMemo(
		() => {
			if (!isCarrierType || readOnly || disabled) return <></>;

			return (
				<InputAdornment position={"end"}>
					<IconButton onClick={handleOpenScanner}>{<Icons.CarrierCode />}</IconButton>
				</InputAdornment>
			);
		},
		// eslint-disable-next-line
		[isCarrierType, readOnly, disabled, handleOpenScanner]
	);

	/** 渲染 Dialog（必須在組件中呼叫）*/
	const renderDialog = useMemo(
		() => {
			if (!isCarrierType || readOnly || disabled) return <></>;
			return <MobileCarrierBarCodeDialog open={isOpen} sourceData={{ onSave: handleConfirmClick }} onClose={handleCloseScanner} />;
		},
		// eslint-disable-next-line
		[isCarrierType, readOnly, disabled, isOpen, handleConfirmClick, handleCloseScanner]
	);

	return {
		renderEndAdornment,
		renderDialog,
	};
};

export default useTextInputCarrier;
